/**
 * Official PathKit Master Logo
 * Production Ready | Brand Locked | Enterprise Grade
 *
 * Variants: primary, monochrome, inverted, icon
 * All geometry is pixel-grid aligned, mathematically intentional,
 * and legible down to 16px.
 */

/** Canonical PathKit brand color tokens */
export const PK_COLORS = {
  navy: '#243850',
  teal: '#55989E',
  neutralLight: '#F5F7FA',
  neutralDark: '#0F1D2E',
} as const;

type LogoVariant = 'primary' | 'monochrome' | 'inverted' | 'icon';

interface PathkitLogoProps {
  /** Visual variant of the logo mark */
  variant?: LogoVariant;
  /** Tailwind class controlling dimensions (default: w-8 h-8) */
  className?: string;
  /**
   * Override color for the monochrome variant.
   * Accepts any CSS color value. Defaults to currentColor.
   */
  monoColor?: string;
}

/**
 * Render the full-mark SVG (frame + P + K + accent).
 * Used by primary, monochrome and inverted variants.
 */
function FullMark({
  navy,
  teal,
  className,
}: {
  navy: string;
  teal: string;
  className: string;
}) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 512 512"
      className={className}
      aria-label="PathKit logo"
      role="img"
    >
      {/* Frame */}
      <path
        d="M0 0h512v512H0zm16 16v480h480V16z"
        fill={navy}
        fillRule="evenodd"
      />
      {/* P letterform with counter */}
      <path
        d="M48 48h176a48 48 0 0 1 48 48v96a48 48 0 0 1-48 48H104v200H48zM104 104h88a24 24 0 0 1 24 24v32a24 24 0 0 1-24 24H104z"
        fill={navy}
        fillRule="evenodd"
      />
      {/* K letterform */}
      <path d="M272 200l176-152v56L312 248l136 144v56L272 296z" fill={teal} />
      {/* Accent square */}
      <rect x="48" y="448" width="56" height="48" fill={teal} />
    </svg>
  );
}

/**
 * Render the icon-only SVG (no frame) optimised for small sizes.
 * Geometry is simplified so letterforms remain legible at 16 px.
 */
function IconMark({
  navy,
  teal,
  className,
}: {
  navy: string;
  teal: string;
  className: string;
}) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 448 448"
      className={className}
      aria-label="PathKit icon"
      role="img"
    >
      {/* P letterform — wider proportions for small-size clarity */}
      <path
        d="M0 0h160a56 56 0 0 1 56 56v80a56 56 0 0 1-56 56H64v256H0zM64 56h80a24 24 0 0 1 24 24v32a24 24 0 0 1-24 24H64z"
        fill={navy}
        fillRule="evenodd"
      />
      {/* K letterform */}
      <path
        d="M224 152l176-152v64L264 200l136 144v64L224 264z"
        fill={teal}
      />
      {/* Accent square */}
      <rect x="0" y="400" width="48" height="48" fill={teal} />
    </svg>
  );
}

export function PathkitLogo({
  variant = 'primary',
  className = 'w-8 h-8',
  monoColor,
}: PathkitLogoProps) {
  switch (variant) {
    case 'monochrome': {
      const c = monoColor ?? 'currentColor';
      return <FullMark navy={c} teal={c} className={className} />;
    }

    case 'inverted':
      return (
        <FullMark
          navy="#FFFFFF"
          teal="#FFFFFF"
          className={className}
        />
      );

    case 'icon':
      return (
        <IconMark
          navy={PK_COLORS.navy}
          teal={PK_COLORS.teal}
          className={className}
        />
      );

    case 'primary':
    default:
      return (
        <FullMark
          navy={PK_COLORS.navy}
          teal={PK_COLORS.teal}
          className={className}
        />
      );
  }
}
